import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Divider } from "antd";

import { Button } from "shared/components/Button";
import FlexContainer from "shared/components/FlexContainer";
import { UnderlineLink } from "shared/components/Links/UnderlineLink/styled";
import { MediumTitle, SmallText } from "shared/components/Typography";

import Well from "shared/components/Well";
import { COLORS, MEDIA_QUERIES } from "shared/config/constants";
import { Calculating, EmptyBox } from "shared/icons";

export const WellContainer = styled(Well)<{ showLumen?: boolean }>`
  background: ${COLORS.grayTheme.activeTextColor};
  border-radius: 4px 4px 0 0;
  ${({ showLumen }) =>
    showLumen &&
    `
     border-radius: 4px;
  `}
  border: none;
  .ant-popover-arrow {
    background-color: red !important;
  }
`;

export const NoLumenTitle = styled(MediumTitle)`
  color: ${COLORS.lightGray4} !important;
  display: flex;
  align-items: center;
`;

export const ActionMainButton = styled(Button)<{
  color?: string;
  backgroundColor?: string;
}>`
  color: ${({ color }) => color} !important;
  background: ${({ backgroundColor }) => backgroundColor} !important;
  &:disabled {
    color: ${({ color }) => color} !important;
    background: ${({ backgroundColor }) => backgroundColor} !important;
    opacity: 0.3;
    pointer-events: none;
  }
`;

export const MoreActionsButton = styled(Button)<{ $lgPri?: boolean }>`
  border: 1px solid white;
  margin-left: 16px;
  ${({ $lgPri }) =>
    $lgPri &&
    `
    height: 40px !important;
    margin-left: 0;
    border-radius: 6px !important;
    background: transparent !important;
    border: 1px solid rgba(255, 255, 255, 0.34) !important;
    color: #edebf5 !important;
    font: 600 13px 'Work Sans', sans-serif;
    letter-spacing: 0.02em;
    &:hover,
    &:focus {
      background: rgba(255, 255, 255, 0.06) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
      color: #edebf5 !important;
    }
  `}
`;

export type LgPriVerdict =
  | "approve"
  | "decline"
  | "cancel"
  | "conditional"
  | "neutral";

const LG_PRI_VERDICT = {
  approve: { background: "#67D6A6", color: "#12352A", border: "none" },
  decline: { background: "#F0728C", color: "#3A0E1B", border: "none" },
  cancel: {
    background: "transparent",
    color: "#EDEBF5",
    border: "1px solid rgba(255, 255, 255, 0.44)",
  },
  conditional: { background: "#F0BE7A", color: "#3A2A0E", border: "none" },
  neutral: {
    background: "transparent",
    color: "#EDEBF5",
    border: "1px solid rgba(255, 255, 255, 0.34)",
  },
} as const;

export const LgPriButton = styled(Button)<{ $verdict: LgPriVerdict }>`
  height: 40px !important;
  padding: 0 20px !important;
  border-radius: 6px !important;
  font: 600 13px "Work Sans", sans-serif;
  letter-spacing: 0.02em;
  background: ${({ $verdict }) => LG_PRI_VERDICT[$verdict].background} !important;
  color: ${({ $verdict }) => LG_PRI_VERDICT[$verdict].color} !important;
  border: ${({ $verdict }) => LG_PRI_VERDICT[$verdict].border} !important;

  &:hover,
  &:focus {
    background: ${({ $verdict }) => LG_PRI_VERDICT[$verdict].background} !important;
    color: ${({ $verdict }) => LG_PRI_VERDICT[$verdict].color} !important;
    border: ${({ $verdict }) => LG_PRI_VERDICT[$verdict].border} !important;
    opacity: 0.92;
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.12) !important;
    color: #8e89a8 !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    cursor: not-allowed;
    opacity: 1;
    pointer-events: auto;
  }
`;

export const PopoverContainer = styled.div`
  background: ${COLORS.grayTheme.activeTextColor};
`;

export const StyledActionButton = styled(Button)<{
  color?: string;
}>`
  color: ${({ color }) => color} !important;
  width: 100% !important;
  justify-content: start;
  &:disabled {
    color: ${({ color }) => color} !important;
    background-color: ${COLORS.primary} !important;
    opacity: 0.3;
    pointer-events: none;
  }
`;

export const LumenSublabel = styled(UnderlineLink)<{
  color?: string;
  disabled?: boolean;
}>`
  color: ${COLORS.disabledText};
  font-size: 12px;
  border-bottom: ${({ disabled }) => disabled && "0px"};
`;

export const Subtitle = styled(SmallText)`
  color: white !important;
`;

export const EmptyBoxWhite = styled(EmptyBox)`
  path {
    fill: white;
  }
`;

export const CalculatingWhite = styled(Calculating)`
  path {
    fill: white;
  }
`;

export const ResponsiveFlexContainer = styled(FlexContainer)`
  ${MEDIA_QUERIES.w.ltLg} {
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    flex: 1 1 auto;
  }
  & > header.vega {
    padding: 0;
  }
`;

// Cosign Action Button Styles
export type CosignStatus = 'available' | 'pending' | 'approved' | 'denied' | 'canceled' | 'expired';

const getCosignButtonStyles = (status: CosignStatus) => {
  switch (status) {
    case 'approved':
      return {
        background: 'transparent',
        border: '1.5px solid #10B981',
        iconBg: '#10B981',
        iconColor: '#fff',
        titleColor: '#10B981',
        subtitleColor: '',
        hasSubtitle: false,
      };
    case 'pending':
      return {
        background: '#4c1d1d',
        border: '1.5px solid #7f1d1d',
        iconBg: '#991b1b',
        iconColor: '#fca5a5',
        titleColor: '#fca5a5',
        subtitleColor: '#9ca3af',
        hasSubtitle: true,
      };
    case 'denied':
      return {
        background: '#4c1d1d',
        border: '1.5px solid #7f1d1d',
        iconBg: '#991b1b',
        iconColor: '#fca5a5',
        titleColor: '#fca5a5',
        subtitleColor: '#9ca3af',
        hasSubtitle: true,
      };
    case 'canceled':
      return {
        background: '#1f2937',
        border: '1.5px solid #374151',
        iconBg: '#374151',
        iconColor: '#9ca3af',
        titleColor: '#9ca3af',
        subtitleColor: '#6b7280',
        hasSubtitle: true,
      };
    case 'available':
    default:
      return {
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        border: 'none',
        iconBg: '#3B82F6',
        iconColor: '#fff',
        titleColor: '#fff',
        subtitleColor: '#9ca3af',
        hasSubtitle: true,
      };
  }
};

export const CosignActionButtonContainer = styled.button<{ status: CosignStatus }>`
  display: flex;
  align-items: center;
  margin:4px;
  gap: 4px;
  padding: 12px 6px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 200px;
  max-width: 240px;
  transition: all 0.2s ease;
  background: ${({ status }) => getCosignButtonStyles(status).background};
  border: ${({ status }) => getCosignButtonStyles(status).border};

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

export const CosignIconWrapper = styled.div<{ status: CosignStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background-color: ${({ status }) => getCosignButtonStyles(status).iconBg};
  color: ${({ status }) => getCosignButtonStyles(status).iconColor};
  flex-shrink: 0;
`;

export const CosignButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
`;

export const CosignButtonTitle = styled.span<{ status: CosignStatus }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ status }) => getCosignButtonStyles(status).titleColor};
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const CosignButtonSubtitle = styled.span<{ status: CosignStatus }>`
  font-size: 12px;
  color: ${({ status }) => getCosignButtonStyles(status).subtitleColor};
  margin-top: 2px;
`;

export const CosignExternalIcon = styled.span<{ status: CosignStatus }>`
  display: flex;
  align-items: center;
  color: ${({ status }) => {
    const styles = getCosignButtonStyles(status);
    return status === 'available' ? '#9ca3af' : styles.titleColor;
  }};
  opacity: 0.7;
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

export const CosignLoader = styled.span<{ status: CosignStatus }>`
  display: flex;
  align-items: center;
  width: 16px;
  height: 16px;
  border: 2px solid;
  border-color: ${({ status }) => {
    const styles = getCosignButtonStyles(status);
    const color = status === 'available' ? '#9ca3af' : styles.titleColor;
    // Convert hex to rgba with 25% opacity
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.25)`;
  }};
  border-top-color: ${({ status }) => {
    const styles = getCosignButtonStyles(status);
    return status === 'available' ? '#9ca3af' : styles.titleColor;
  }};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  opacity: 0.7;
`;

